import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { View, StyleSheet, TouchableOpacity, Text } from "react-native";
import {
  useGetCoursesSummaryBySchoolQuery,
  useGetCourseAnalyticsByIdQuery,
  useGetModuleAnalyticsByCourseQuery,
  useGetActivityAnalyticsByModuleQuery,
  useGetObjectiveAnalyticsByActivityQuery,
  useGetCourseCompletionSummaryBySchoolQuery,
  useGetCourseCompletionDetailsQuery,
  useGetModuleCompletionDetailsQuery,
  useGetActivityCompletionDetailsQuery,
  useSchoolsQuery,
  useGetCourseHierarchyQuery,
  useGetUserPermissionsQuery,
} from "../graphql/generated/graphql";
import NoChatBarLayout from "../layouts/NoChatBarLayout.web";
import BubbleChartContainer from "../containers/analytics/BubbleChartContainer/BubbleChartContainer";
import AccordionAnalyticsContainer from "../containers/analytics/AccordionAnalyticsContainer.js/AccordionAnalyticsContainer";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import AnalyticsSearchBar from "../components/analytics/AnalyticsSearchBar/AnalyticsSearchBar";
import { MotiView } from "moti";
import { useFocusEffect, useRoute, useNavigation } from "@react-navigation/native";
import ActivityInsights from "../components/analytics/ActivityInsights/ActivityInsights";
import { RolePermissions } from "../constants/rolesAndPermissions";
// GraphQL logging helper
const logQuery = (queryName, variables, data) => {
  console.log(`GraphQL Query Executed: ${queryName}`, {
    variables,
    results: data ? `Received data with ${Array.isArray(data) ? data.length : 1} items` : "No data received",
  });
};

const computeGlobalExtremes = (data) => {
  let maxAbsX = 0;
  let maxAbsY = 0;
  const processItem = (item) => {
    const s = item.success_points || 0;
    const c = item.confusion_points || 0;
    const q = item.questions_asked || 0;
    const a = item.active_interaction_count || 0;
    const d = item.distractions || 0;
    const xVal = s - c;
    const yVal = q + a - d;
    maxAbsX = Math.max(maxAbsX, Math.abs(xVal));
    maxAbsY = Math.max(maxAbsY, Math.abs(yVal));
    if (item.details) {
      ["modules", "activities", "objectives"].forEach((key) => {
        if (item.details[key]) {
          item.details[key].forEach(processItem);
        }
      });
    }
  };
  data.forEach(processItem);
  return { maxAbsX: Math.max(1, maxAbsX), maxAbsY: Math.max(1, maxAbsY) };
};

const mapDataWithGlobalScale = (data, level, globalScale) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn(`No valid data to map for level: ${level}`);
    return [];
  }
  const { maxAbsX, maxAbsY } = globalScale;
  return data.map((item, index) => {
    const s = item.success_points || 0;
    const c = item.confusion_points || 0;
    const q = item.questions_asked || 0;
    const a = item.active_interaction_count || 0;
    const d = item.distractions || 0;
    const xVal = ((s - c) / maxAbsX) * 10;
    const yVal = ((q + a - d) / maxAbsY) * 10;
    const sentiment = item.sentiment || "neutral";
    const enrollment = item.enrollment || 1;
    return {
      x: xVal,
      y: yVal,
      size: Math.max((enrollment / 300) * 30, 10),
      color:
        sentiment === "positive"
          ? "#85E139"
          : sentiment === "mixed"
            ? "#0084FF"
            : sentiment === "negative"
              ? "#F63D68"
              : "#0084FF",
      label: item.label || `Item ${index + 1}`,
      level,
      id: item.id,
      ...item,
    };
  });
};

const mergeCompletionData = (analyticsData, completionData) => {
  if (!analyticsData || !completionData || !completionData.length) {
    return analyticsData;
  }
  return analyticsData.map((item) => {
    const completionItem = completionData.find((c) => c.id === item.id);
    if (completionItem) {
      return { ...item, completionStats: completionItem.completionStats };
    }
    return item;
  });
};

const AnalyticsView = () => {
  const { data: userPermissionsData } = useGetUserPermissionsQuery();
  const isAdmin = userPermissionsData?.getUserPermissions.includes(RolePermissions.analyticsViewSchool);

  const { data: schoolsData } = useSchoolsQuery();
  const schoolId = schoolsData?.getSchools[0]?.id;

  const [currentData, setCurrentData] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [globalScale, setGlobalScale] = useState({ maxAbsX: 10, maxAbsY: 10 });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [expandedItemIds, setExpandedItemIds] = useState({});
  const [childrenData, setChildrenData] = useState({});
  const [loadingChildren, setLoadingChildren] = useState({});
  const [dataLoaded, setDataLoaded] = useState({
    course: false,
    module: false,
    activity: false,
    objective: false,
  });
  const containerRef = useRef(null);
  const route = useRoute();
  const courseId = route.params?.courseId;
  const moduleId = route.params?.moduleId;
  const activityId = route.params?.activityId;
  const isGlobalLevel = !courseId && !moduleId && !activityId;
  const isCourseLevel = !moduleId && !activityId && !!courseId;
  const isModuleLevel = !activityId && !!moduleId && !!courseId;
  const isActivityLevel = !!activityId && !!moduleId && !!courseId;

  const navigation = useNavigation();
  // Extract refetch functions to always re-fetch fresh data
  const {
    data: courseDetailData,
    loading: courseDetailLoading,
    refetch: refetchCourseDetail,
  } = useGetCourseAnalyticsByIdQuery({
    variables: { courseId },
    skip: !courseId,
    onCompleted: (data) => {
      logQuery("getCourseAnalyticsById", { courseId }, data?.getCourseAnalyticsById?.data);
      setDataLoaded((prev) => ({ ...prev, course: true }));
    },
  });

  const {
    data: moduleData,
    loading: moduleLoading,
    refetch: refetchModuleData,
  } = useGetModuleAnalyticsByCourseQuery({
    variables: { courseId },
    skip: !courseId,
    onCompleted: (data) => {
      logQuery("getModuleAnalyticsByCourse", { courseId }, data?.getModuleAnalyticsByCourse?.data);
      setDataLoaded((prev) => ({ ...prev, module: true }));
    },
  });

  const {
    data: activityData,
    loading: activityLoading,
    refetch: refetchActivityData,
  } = useGetActivityAnalyticsByModuleQuery({
    variables: { moduleId },
    skip: !moduleId || !isActivityLevel,
    onCompleted: (data) => {
      logQuery("getActivityAnalyticsByModule", { moduleId }, data?.getActivityAnalyticsByModule?.data);
      setDataLoaded((prev) => ({ ...prev, activity: true }));
    },
  });

  const {
    data: objectiveData,
    loading: objectiveLoading,
    refetch: refetchObjective,
  } = useGetObjectiveAnalyticsByActivityQuery({
    variables: { aiTutorId: activityId },
    skip: !activityId,
    onCompleted: (data) => {
      logQuery(
        "getObjectiveAnalyticsByActivity",
        { aiTutorId: activityId },
        data?.getObjectiveAnalyticsByActivity?.data
      );
      setDataLoaded((prev) => ({ ...prev, objective: true }));
    },
  });

  const {
    data: courseSummaryData,
    loading: courseSummaryLoading,
    refetch: refetchCourseSummary,
  } = useGetCoursesSummaryBySchoolQuery({
    variables: { schoolId },
    // We always want to fetch summary when at course level
    skip: !schoolId,
    onCompleted: (data) => {
      logQuery("getCoursesSummaryBySchool", { schoolId }, data?.getCoursesSummaryBySchool?.data);
    },
  });

  const {
    data: completionData,
    loading: completionLoading,
    refetch: refetchCompletionSummary,
  } = useGetCourseCompletionSummaryBySchoolQuery({
    variables: { schoolId },
    skip: !schoolId || !isCourseLevel || !!courseId,
    onCompleted: (data) => {
      logQuery("getCourseCompletionSummaryBySchool", { schoolId }, data?.getCourseCompletionSummaryBySchool?.data);
    },
  });

  const {
    data: courseCompletionDetailData,
    loading: courseCompletionDetailLoading,
    refetch: refetchCourseCompletionDetail,
  } = useGetCourseCompletionDetailsQuery({
    variables: { courseId },
    skip: !courseId,
    onCompleted: (data) => {
      logQuery("getCourseCompletionDetails", { courseId }, data?.getCourseCompletionDetails?.data);
    },
  });

  const {
    data: moduleCompletionData,
    loading: moduleCompletionLoading,
    refetch: refetchModuleCompletion,
  } = useGetModuleCompletionDetailsQuery({
    variables: { moduleId },
    skip: !moduleId,
    onCompleted: (data) => {
      logQuery("getModuleCompletionDetails", { moduleId }, data?.getModuleCompletionDetails?.data);
    },
  });

  const {
    data: activityCompletionData,
    loading: activityCompletionLoading,
    refetch: refetchActivityCompletion,
  } = useGetActivityCompletionDetailsQuery({
    variables: { aiTutorId: activityId },
    skip: !activityId,
    onCompleted: (data) => {
      logQuery("getActivityCompletionDetails", { aiTutorId: activityId }, data?.getActivityCompletionDetails?.data);
    },
  });

  // Remove objectives since we don't want them in the search hierarchy
  const { data: hierarchyData, loading: hierarchyLoading } = useGetCourseHierarchyQuery({
    variables: { schoolId },
    skip: !schoolId,
    onCompleted: (data) => {
      logQuery("getCourseHierarchy", { schoolId }, data?.getCourseHierarchy?.data);
    },
  });

  const getCurrentCourse = () => {
    if (!courseId || !courseDetailData?.getCourseAnalyticsById?.data) return null;
    return courseDetailData.getCourseAnalyticsById.data.find((course) => course.id === courseId);
  };
  const getCurrentModule = () => {
    if (!moduleId || !moduleData?.getModuleAnalyticsByCourse?.data) return null;
    return moduleData.getModuleAnalyticsByCourse.data.find((module) => module.id === moduleId);
  };
  const getCurrentActivity = () => {
    if (!activityId || !activityData?.getActivityAnalyticsByModule?.data) return null;
    return activityData.getActivityAnalyticsByModule.data.find((activity) => activity.id === activityId);
  };

  const currentCourse = getCurrentCourse()
    ? { label: getCurrentCourse().label, level: "course", id: getCurrentCourse().id }
    : { label: "course", id: courseId, level: "course" };
  const currentModule = getCurrentModule()
    ? { label: getCurrentModule().label, level: "module", id: getCurrentModule().id }
    : { label: "module", id: moduleId, level: "module" };
  const currentActivity = getCurrentActivity()
    ? { label: getCurrentActivity().label, level: "activity", id: getCurrentActivity().id }
    : { label: "activity", id: activityId, level: "activity" };

  // Process the hierarchy data to remove objectives for the search bar
  const searchHierarchyData = useMemo(() => {
    if (!hierarchyData?.getCourseHierarchy?.data) return [];

    // Create a deep copy and modify it to exclude objectives
    return hierarchyData.getCourseHierarchy.data.map((course) => ({
      id: course.id,
      label: course.label,
      level: course.level,
      details: {
        modules: course.details.modules.map((module) => ({
          id: module.id,
          label: module.label,
          level: module.level,
          parentCourse: module.parentCourse,
          details: {
            activities: module.details.activities.map((activity) => ({
              id: activity.id,
              label: activity.label,
              level: activity.level,
              parentModule: activity.parentModule,
              parentCourse: activity.parentCourse,
              // Exclude objectives by not including them in this object
              details: {}, // Empty object or null instead of activity.details
            })),
          },
        })),
      },
    }));
  }, [hierarchyData]);

  const isLoading =
    courseSummaryLoading ||
    completionLoading ||
    courseDetailLoading ||
    moduleLoading ||
    activityLoading ||
    objectiveLoading ||
    courseCompletionDetailLoading ||
    moduleCompletionLoading ||
    hierarchyLoading ||
    activityCompletionLoading;

  // Function to reset data loading state
  const resetDataLoaded = (level) => {
    const newState = { ...dataLoaded };

    // Reset all levels higher than the current one
    if (level === "course" || !level) {
      newState.course = false;
      newState.module = false;
      newState.activity = false;
      newState.objective = false;
    } else if (level === "module") {
      newState.module = false;
      newState.activity = false;
      newState.objective = false;
    } else if (level === "activity") {
      newState.activity = false;
      newState.objective = false;
    } else if (level === "objective") {
      newState.objective = false;
    }

    setDataLoaded(newState);
  };

  // Refetch all data for current view state
  const refetchCurrentView = useCallback(async () => {
    if (isCourseLevel) {
      await refetchCourseDetail({ courseId });
      await refetchCourseCompletionDetail({ courseId });
    } else if (isModuleLevel) {
      await refetchModuleData({ courseId });
      await refetchModuleCompletion({ moduleId: moduleId });
    } else if (isActivityLevel) {
      await refetchActivityData({ moduleId: moduleId });
      await refetchActivityCompletion({ aiTutorId: activityId });
    }
  }, [courseId, moduleId, activityId]);

  // Process course summary data (initial view)
  useEffect(() => {
    if (isGlobalLevel && !courseId && courseSummaryData?.getCoursesSummaryBySchool?.data && !courseSummaryLoading) {
      console.log("Processing course summary data");
      const courseData = courseSummaryData.getCoursesSummaryBySchool.data;
      const scale = computeGlobalExtremes(courseData);
      setGlobalScale(scale);
      let processedData = courseData;
      if (completionData?.getCourseCompletionSummaryBySchool?.data) {
        processedData = mergeCompletionData(courseData, completionData.getCourseCompletionSummaryBySchool.data);
      }
      const transformedData = mapDataWithGlobalScale(processedData, "course", scale);
      setCurrentData(transformedData);
      setBreadcrumb([]);
      setInitialized(true);
    }
  }, [activityId, moduleId, courseId, courseSummaryData, completionData, courseSummaryLoading]);

  // Process course detail data and capture current course
  useEffect(() => {
    if (isCourseLevel && courseDetailData?.getCourseAnalyticsById?.data && !courseDetailLoading && dataLoaded.course) {
      console.log("Processing course detail data for ID:", courseId);
      const courseDetail = courseDetailData.getCourseAnalyticsById.data[0];
      if (courseDetail && courseDetail.details?.modules) {
        let modules = courseDetail.details.modules;
        if (courseCompletionDetailData?.getCourseCompletionDetails?.data) {
          const courseCompletionDetail = courseCompletionDetailData.getCourseCompletionDetails.data[0];
          if (courseCompletionDetail?.details?.modules) {
            modules = modules.map((module) => {
              const completionModule = courseCompletionDetail.details.modules.find((m) => m.id === module.id);
              return completionModule ? { ...module, completionStats: completionModule.completionStats } : module;
            });
          }
        }
        const transformedData = mapDataWithGlobalScale(modules, "module", globalScale);
        setCurrentData(transformedData);
        // Only set breadcrumb if it's empty or the top level doesn't match
        if (breadcrumb.length === 0 || breadcrumb[0]?.id !== courseDetail.id) {
          setBreadcrumb([{ label: courseDetail.label, level: "course", id: courseDetail.id }]);
        }
      }
    }
  }, [
    courseId,
    moduleId,
    activityId,
    courseDetailData,
    courseCompletionDetailData,
    globalScale,
    courseDetailLoading,
    dataLoaded.course,
  ]);

  // Process module data and build breadcrumb using currentCourse if available
  useEffect(() => {
    if (isModuleLevel && dataLoaded.module && dataLoaded.course) {
      console.log("Processing module data for module ID:", moduleId);
      if (moduleData?.getModuleAnalyticsByCourse?.data) {
        const modules = moduleData.getModuleAnalyticsByCourse.data;
        const selectedModule = modules.find((m) => m.id === moduleId);
        if (selectedModule && selectedModule.details?.activities) {
          let activities = selectedModule.details.activities;
          if (moduleCompletionData?.getModuleCompletionDetails?.data) {
            const moduleCompletionDetail = moduleCompletionData.getModuleCompletionDetails.data[0];
            if (moduleCompletionDetail?.details?.activities) {
              activities = activities.map((activity) => {
                const completionActivity = moduleCompletionDetail.details.activities.find((a) => a.id === activity.id);
                return completionActivity
                  ? { ...activity, completionStats: completionActivity.completionStats }
                  : activity;
              });
            }
          }
          const transformedData = mapDataWithGlobalScale(activities, "activity", globalScale);
          setCurrentData(transformedData);
          // Only update breadcrumb if needed
          if (breadcrumb.length < 2 || breadcrumb[1]?.id !== selectedModule.id) {
            setBreadcrumb([currentCourse, { label: selectedModule.label, level: "module", id: selectedModule.id }]);
          }
        }
      }
    }
  }, [courseId, moduleId, activityId, moduleData, moduleCompletionData, globalScale, moduleLoading, dataLoaded.module]);

  // Process activity data and build breadcrumb as [currentCourse, currentModule, activity]
  useEffect(() => {
    if (isActivityLevel && activityId && dataLoaded.activity && dataLoaded.module && dataLoaded.course) {
      console.log("Processing activity data");
      if (activityData?.getActivityAnalyticsByModule?.data) {
        const activities = activityData.getActivityAnalyticsByModule.data;
        const selectedActivityData = activities.find((a) => a.id === activityId);
        if (selectedActivityData && selectedActivityData.details?.objectives) {
          let objectives = selectedActivityData.details.objectives.map((obj) => ({ ...obj }));
          if (activityCompletionData?.getActivityCompletionDetails?.data) {
            const activityCompletionDetail = activityCompletionData.getActivityCompletionDetails.data[0];
            if (activityCompletionDetail?.details?.objectives) {
              objectives = objectives.map((objective) => {
                const completionObjective = activityCompletionDetail.details.objectives.find(
                  (o) => o.id === objective.id
                );
                return completionObjective
                  ? { ...objective, completionStats: completionObjective.completionStats }
                  : objective;
              });
            }
            setSelectedActivity({
              ...selectedActivityData,
              completionStats: activityCompletionDetail.completionStats,
            });
          } else {
            setSelectedActivity({ ...selectedActivityData });
          }
          const transformedData = mapDataWithGlobalScale(objectives, "objective", globalScale);
          setCurrentData(transformedData);
          // Only update breadcrumb if needed
          if (breadcrumb.length < 3 || breadcrumb[2]?.id !== selectedActivityData.id) {
            setBreadcrumb([
              currentCourse,
              currentModule,
              { label: selectedActivityData.label, level: "activity", id: selectedActivityData.id },
            ]);
          }
        }
      }
    }
  }, [
    courseId,
    moduleId,
    activityId,
    activityData,
    activityCompletionData,
    globalScale,
    activityLoading,
    moduleData,
    dataLoaded.activity,
  ]);

  // Clear children data on navigation to force re-fetching every time
  const clearChildrenData = () => {
    setChildrenData({});
    setExpandedItemIds({});
  };

  // Handle route params for direct navigation
  useFocusEffect(
    useCallback(() => {
      if (initialized && courseId) {
        clearChildrenData();

        if (courseId && moduleId && activityId) {
          resetDataLoaded("activity");
        } else if (courseId && moduleId) {
          resetDataLoaded("module");
        } else if (courseId) {
          resetDataLoaded("course");
        }
      }
    }, [courseId, moduleId, activityId, initialized])
  );

  // Handle drill down clicks (forwards navigation)
  const handleDrillDown = (clickedItem) => {
    if (!clickedItem || !clickedItem.level) {
      console.warn("Invalid item clicked:", clickedItem);
      return;
    }
    console.log("Drill down clicked:", clickedItem.level, clickedItem.id, clickedItem.label);
    clearChildrenData();

    if (clickedItem.level === "course") {
      resetDataLoaded("course");
      setBreadcrumb([{ label: clickedItem.label, level: "course", id: clickedItem.id }]);
      navigation.replace("Analytics", {
        courseId: clickedItem.id,
      });
    } else if (clickedItem.level === "module") {
      resetDataLoaded("module");
      // Build breadcrumb as [currentCourse, module]
      setBreadcrumb([currentCourse, { label: clickedItem.label, level: "module", id: clickedItem.id }]);
      navigation.replace("Analytics", {
        courseId: currentCourse.id,
        moduleId: clickedItem.id,
      });
    } else if (clickedItem.level === "activity") {
      resetDataLoaded("activity");

      // Check if we have parentModule information from the search
      const parentModule = clickedItem.parentModule;
      // Use parentModule from clickedItem if available, otherwise try to find from moduleData
      const moduleItem = parentModule
        ? { label: parentModule.label, level: "module", id: parentModule.id }
        : {
            label:
              moduleData?.getModuleAnalyticsByCourse?.data?.find((m) => m.id === moduleId)?.label ||
              breadcrumb[1]?.label ||
              "module",
            id: parentModule?.id || moduleId,
            level: "module",
          };
      setBreadcrumb([currentCourse, moduleItem, { label: clickedItem.label, level: "activity", id: clickedItem.id }]);
      navigation.replace("Analytics", {
        courseId: currentCourse.id,
        moduleId: moduleItem.id,
        activityId: clickedItem.id,
      });
    }
  };

  // Handle breadcrumb navigation (backwards)
  const handleBreadcrumbClick = (index) => {
    if (index < 0 || index >= breadcrumb.length) return;
    clearChildrenData();
    const target = breadcrumb[index];
    console.log("Breadcrumb click:", target.level, target.id);
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newBreadcrumb);

    if (target.level === "course") {
      resetDataLoaded("course");
      navigation.replace("Analytics", {
        courseId: target.id,
      });
    } else if (target.level === "module") {
      resetDataLoaded("module");
      navigation.replace("Analytics", {
        courseId: breadcrumb[0].id,
        moduleId: target.id,
      });
    } else if (target.level === "activity") {
      resetDataLoaded("activity");
      navigation.replace("Analytics", {
        courseId: breadcrumb[0].id,
        moduleId: breadcrumb[1].id,
        activityId: target.id,
      });
    }
  };

  // Back to all courses
  const handleBackToAllCourses = () => {
    if (!isAdmin) {
      return;
    }

    clearChildrenData();
    resetDataLoaded();
    setBreadcrumb([]);
    setSelectedActivity(null);
    navigation.replace("Analytics", {});
  };

  // Effect to mark data as loaded after page state changes
  useEffect(() => {
    if (isCourseLevel && courseId) {
      setDataLoaded((prev) => ({ ...prev, course: true }));
    } else if (isModuleLevel && moduleId) {
      setDataLoaded((prev) => ({ ...prev, module: true }));
    } else if (isActivityLevel && activityId) {
      setDataLoaded((prev) => ({ ...prev, activity: true }));
    }
  }, [courseId, moduleId, activityId]);

  // loadChildData always re-fetches fresh data based on level
  const loadChildData = async (itemId, level) => {
    setLoadingChildren((prev) => ({ ...prev, [itemId]: true }));
    try {
      console.log(`Loading children for ${level} with ID ${itemId}`);
      if (level === "course") {
        const result = await refetchCourseDetail({ courseId: itemId });
        if (result.data?.getCourseAnalyticsById?.data?.[0]?.details?.modules) {
          const modules = result.data.getCourseAnalyticsById.data[0].details.modules;
          setChildrenData((prev) => ({ ...prev, [itemId]: modules }));
        }
      } else if (level === "module") {
        const result = await refetchActivityData({ moduleId: itemId });
        if (result.data?.getActivityAnalyticsByModule?.data) {
          const activities = result.data.getActivityAnalyticsByModule.data;
          setChildrenData((prev) => ({ ...prev, [itemId]: activities }));
        }
      } else if (level === "activity") {
        const result = await refetchObjective({ aiTutorId: itemId });
        if (result.data?.getObjectiveAnalyticsByActivity?.data) {
          const objectives = result.data.getObjectiveAnalyticsByActivity.data;
          setChildrenData((prev) => ({ ...prev, [itemId]: objectives }));
        }
      }
    } catch (error) {
      console.error(`Error loading children for ${level} ${itemId}:`, error);
    } finally {
      setLoadingChildren((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const titleText = isActivityLevel ? "Activity Insights" : isModuleLevel ? "Module Insights" : "Course Insights";

  const hasValidData = currentData && Array.isArray(currentData) && currentData.length > 0;

  return (
    <NoChatBarLayout>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        transition={{ type: "timing", duration: 500 }}>
        <CustomText useTranslationText={false} size="m" textType="display" text="Analytics" weight="ultraBold" />
      </MotiView>
      <CustomSpacing type="vertical" size="m" />
      <View style={styles.searchContainer}>
        <AnalyticsSearchBar
          data={searchHierarchyData}
          mergedData={searchHierarchyData}
          onDrillDown={handleDrillDown}
          searchTypeDefault="all"
          defaultSearchText=""
          isLoading={isLoading || hierarchyLoading}
          error={null}
        />
      </View>
      <View style={styles.contentContainer}>
        <CustomSpacing type="vertical" size="xl" />
        <View style={styles.breadcrumb}>
          <TouchableOpacity onPress={handleBackToAllCourses} disabled={!isAdmin}>
            <CustomText
              useTranslationText={false}
              size="s"
              text="All Courses"
              style={[
                styles.breadcrumbText,
                styles.breadcrumbLink,
                { color: !isAdmin ? "#ccc" : "#0084FF", textDecorationLine: !isAdmin ? "none" : "underline" },
              ]}
            />
          </TouchableOpacity>
          {breadcrumb.map((crumb, index) => (
            <React.Fragment key={index}>
              <CustomText useTranslationText={false} size="s" text=" > " style={styles.breadcrumbText} />
              <TouchableOpacity onPress={() => handleBreadcrumbClick(index)}>
                <CustomText
                  useTranslationText={false}
                  size="s"
                  text={crumb.label}
                  style={[styles.breadcrumbText, styles.breadcrumbLink]}
                />
              </TouchableOpacity>
            </React.Fragment>
          ))}
        </View>
        <CustomText
          size="xl"
          text="Breakdown & Stats"
          weight="bold"
          useTranslationText={false}
          style={{ paddingBottom: 20 }}
        />
        <View style={styles.container} ref={containerRef}>
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <CustomText useTranslationText={false} textType="text" text="Loading analytics data..." size="m" />
            </View>
          ) : !hasValidData ? (
            <CustomText useTranslationText={false} textType="text" text="No analytics data available" size="m" />
          ) : isActivityLevel && selectedActivity ? (
            <ActivityInsights activityData={currentData} parentActivity={selectedActivity} />
          ) : (
            <>
              <BubbleChartContainer data={currentData} onDrillDown={handleDrillDown} />
              <CustomSpacing type="vertical" size="xxl" />
              <CustomText size="xl" text={titleText} weight="bold" useTranslationText={false} />
              <AccordionAnalyticsContainer
                data={currentData}
                onListDrillDown={handleDrillDown}
                breadcrumb={breadcrumb}
                loadChildData={loadChildData}
                expandedItemIds={expandedItemIds}
                setExpandedItemIds={setExpandedItemIds}
                childrenData={childrenData}
                setChildrenData={setChildrenData}
                loadingChildren={loadingChildren}
                setLoadingChildren={setLoadingChildren}
              />
            </>
          )}
        </View>
      </View>
    </NoChatBarLayout>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    position: "relative",
    zIndex: 1,
  },
  searchContainer: {
    position: "relative",
    zIndex: 1000,
    marginBottom: 16,
  },
  breadcrumb: {
    flexDirection: "row",
    marginBottom: 10,
    position: "relative",
    zIndex: 1,
    flexWrap: "wrap",
    alignItems: "center",
  },
  breadcrumbText: {
    color: "#6B7280",
    fontWeight: "500",
  },
  breadcrumbLink: {
    color: "#0084FF",
    textDecorationLine: "underline",
  },
  container: {
    flex: 1,
    width: "100%",
    position: "relative",
    zIndex: 1,
  },
  loadingContainer: {
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default AnalyticsView;
